import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Form } from 'react-bootstrap';
import { getEventById } from '../../lib/api/events';
import {
  createEventGroup,
  deleteEventGroup,
  listEventGroupMembers,
  listEventGroups,
  setEventMobileAppEnabled,
} from '../../lib/api/groups';
import { useToast } from '../../contexts/ToastContext';
import { captureError } from '../../lib/capture-error';
import type { EventGroup, EventWithDetails, GroupMember } from '../../types/database';

const AdminEventGroupsPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const { showToast } = useToast();
  const [event, setEvent] = useState<EventWithDetails | null>(null);
  const [groups, setGroups] = useState<EventGroup[]>([]);
  const [members, setMembers] = useState<GroupMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);
  const [togglingMobile, setTogglingMobile] = useState(false);

  const loadData = useCallback(async () => {
    if (!eventId) return;
    try {
      setLoading(true);
      setError(null);
      const [eventData, groupData, memberData] = await Promise.all([
        getEventById(eventId),
        listEventGroups(eventId),
        listEventGroupMembers(eventId),
      ]);
      setEvent(eventData);
      setGroups(groupData);
      setMembers(memberData);
    } catch (err) {
      captureError(err, 'Error loading event groups');
      setError('Failed to load groups');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const memberCount = (groupId: string) =>
    members.filter((m) => m.group_id === groupId).length;

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!eventId || !newName.trim()) return;
    setSaving(true);
    try {
      const created = await createEventGroup(eventId, {
        name: newName.trim(),
        sort_order: groups.length + 1,
      });
      setGroups([...groups, created]);
      setNewName('');
      showToast(`Created ${created.name}`, 'success');
    } catch (err) {
      captureError(err, 'Error creating group');
      showToast(err instanceof Error ? err.message : 'Failed to create group', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (group: EventGroup) => {
    if (!window.confirm(`Delete “${group.name}”? Members and itinerary stops will be removed.`)) {
      return;
    }
    try {
      await deleteEventGroup(group.id);
      setGroups(groups.filter((g) => g.id !== group.id));
      setMembers(members.filter((m) => m.group_id !== group.id));
      showToast(`Deleted ${group.name}`, 'success');
    } catch (err) {
      captureError(err, 'Error deleting group');
      showToast('Failed to delete group', 'error');
    }
  };

  const handleToggleMobile = async (enabled: boolean) => {
    if (!eventId || !event) return;
    setTogglingMobile(true);
    try {
      await setEventMobileAppEnabled(eventId, enabled);
      setEvent({ ...event, mobile_app_enabled: enabled });
      showToast(
        enabled ? 'Mobile field app enabled for this event' : 'Mobile field app disabled',
        'success'
      );
    } catch (err) {
      captureError(err, 'Error toggling mobile_app_enabled');
      showToast('Failed to update mobile app setting', 'error');
    } finally {
      setTogglingMobile(false);
    }
  };

  if (loading) {
    return (
      <div className="container my-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading…</span>
        </div>
      </div>
    );
  }

  if (error || !event || !eventId) {
    return (
      <div className="container my-5">
        <div className="alert alert-danger">{error || 'Event not found'}</div>
        <Link to="/admin" className="btn btn-outline-secondary">
          Back to admin
        </Link>
      </div>
    );
  }

  return (
    <div className="container my-5">
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
        <div>
          <Link to="/admin" className="btn btn-outline-secondary btn-sm mb-2">
            ← Admin
          </Link>
          <h1 className="h3 mb-1">Field groups</h1>
          <p className="text-muted mb-0">{event.title}</p>
        </div>
        <Link
          to={`/admin/events/${eventId}/applications`}
          className="btn btn-outline-primary btn-sm"
        >
          Applications
        </Link>
      </div>

      <div className="card mb-4">
        <div className="card-body d-flex flex-wrap align-items-center justify-content-between gap-3">
          <div>
            <div className="fw-semibold">Mobile field app</div>
            <div className="small text-muted">
              When enabled, approved attendees can open the Expo app for this event.
            </div>
          </div>
          <Form.Check
            type="switch"
            id="mobile-app-enabled"
            label={event.mobile_app_enabled ? 'Enabled' : 'Disabled'}
            checked={!!event.mobile_app_enabled}
            disabled={togglingMobile}
            onChange={(e) => handleToggleMobile(e.target.checked)}
          />
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-header">Create group</div>
        <div className="card-body">
          <form className="row g-2 align-items-end" onSubmit={handleCreate}>
            <div className="col-md-8">
              <label className="form-label" htmlFor="group-name">
                Name
              </label>
              <input
                id="group-name"
                className="form-control"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. Group 3"
                required
              />
            </div>
            <div className="col-md-4">
              <button type="submit" className="btn btn-primary w-100" disabled={saving}>
                {saving ? 'Creating…' : 'Add group'}
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="card">
        <div className="card-header d-flex justify-content-between">
          <span>Groups ({groups.length})</span>
          <span className="text-muted small">{members.length} assigned</span>
        </div>
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead>
              <tr>
                <th>Name</th>
                <th>Members</th>
                <th>Order</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {groups.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-muted text-center py-4">
                    No groups yet. Create one above, or run{' '}
                    <code>npm run seed:mobile-poc</code>.
                  </td>
                </tr>
              ) : (
                groups.map((group) => (
                  <tr key={group.id}>
                    <td className="fw-semibold">{group.name}</td>
                    <td>{memberCount(group.id)}</td>
                    <td>{group.sort_order}</td>
                    <td className="text-end">
                      <Link
                        to={`/admin/events/${eventId}/groups/${group.id}`}
                        className="btn btn-sm btn-outline-primary me-2"
                      >
                        Manage
                      </Link>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDelete(group)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminEventGroupsPage;
